import React, { useState } from 'react';
import { 
  CheckCircle2, 
  ArrowRight, 
  DollarSign, 
  Sparkles, 
  ShieldCheck, 
  Building2, 
  Sliders, 
  RotateCcw, 
  FileText, 
  Download, 
  Lock, 
  Zap, 
  X,
  ChevronRight,
  TrendingUp,
  Percent
} from 'lucide-react';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';
import { getJurisdictionPreset, JURISDICTION_PRESETS } from '../utils/jurisdictionPresets';

interface QuickStartWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (mode: 'quick' | 'power') => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

export function QuickStartWizard({ isOpen, onClose, onComplete, showToast }: QuickStartWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const activeSettings = getBusinessSettings();

  // Step 1: Country & Tax Jurisdiction
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>(activeSettings.jurisdiction || 'US');
  
  // Step 2: Inflow & Overhead
  const initialRev = selectedJurisdiction === 'ZA' ? 45000 : selectedJurisdiction === 'IN' ? 350000 : 12000;
  const initialExp = selectedJurisdiction === 'ZA' ? 14000 : selectedJurisdiction === 'IN' ? 95000 : 3500;
  const [revenueInput, setRevenueInput] = useState<number>(initialRev);
  const [expenseInput, setExpenseInput] = useState<number>(initialExp);

  if (!isOpen) return null;

  const currentPreset = getJurisdictionPreset(selectedJurisdiction);
  const currencySym = currentPreset.currencySymbol || '$';
  const vatRate = currentPreset.vatRate || 0;

  // Real statutory tax calculation
  const vatAmount = vatRate > 0 
    ? Math.round(revenueInput * (vatRate / (100 + vatRate)))
    : 0;
  const netOperating = Math.max(0, revenueInput - vatAmount - expenseInput);
  const incomeTaxBuffer = Math.round(netOperating * (vatRate > 0 ? 0.18 : 0.22));
  const totalTaxReserve = vatAmount + incomeTaxBuffer;
  const safeSpendableCash = Math.max(0, revenueInput - expenseInput - totalTaxReserve);
  const operatingMargin = revenueInput > 0 ? Math.max(0, ((revenueInput - expenseInput) / revenueInput) * 100) : 0;

  const handleApplyPreset = (rev: number, exp: number, label: string) => {
    setRevenueInput(rev);
    setExpenseInput(exp);
    if (showToast) showToast(`Applied ${label} numbers`, 'info');
  };

  const handleFinish = (mode: 'quick' | 'power') => {
    saveBusinessSettings({
      ...activeSettings,
      jurisdiction: selectedJurisdiction,
      currencyCode: currentPreset.currencyCode || 'USD',
      currencySymbol: currentPreset.currencySymbol || '$',
    });
    if (showToast) {
      showToast(`✨ Workspace ready! ${mode === 'quick' ? 'Quick Clarity Mode' : 'Power OS Mode'} active.`, 'success');
    }
    onComplete(mode);
  };

  const handleDownloadSummary = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Metric,Amount,Notes\n" +
      `Monthly Gross Revenue,${revenueInput},Total projected monthly client receipts\n` +
      `Operating Overhead,${expenseInput},Estimated monthly operational expenses\n` +
      `Locked Tax Reserve,${totalTaxReserve},Mandatory set-aside for ${currentPreset.taxAuthorityLabel}\n` +
      `Safe to Spend Cash,${safeSpendableCash},Net safe owner draw / dividend\n` +
      `Statutory Authority,${currentPreset.taxAuthorityLabel},Jurisdiction: ${currentPreset.name}\n`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `AzuraOS_Tax_Plan_${selectedJurisdiction}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (showToast) showToast("Tax Plan CSV downloaded!", "success");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
      <div 
        className="bg-[#0D1113] border border-[#232B2E] rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl text-white flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#232B2E] bg-[#121719]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/15 flex items-center justify-center text-[#14B8A6]">
              <Zap size={16} />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-tight text-white">10-Second Tax Clarity Setup</h2>
              <p className="text-[11px] text-gray-400">Step {step} of 3 · Zero accounting jargon</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress Tracker */}
        <div className="grid grid-cols-3 gap-1 px-6 pt-4">
          <div className={`h-1.5 rounded-full transition-all ${step >= 1 ? 'bg-[#14B8A6]' : 'bg-[#232B2E]'}`} />
          <div className={`h-1.5 rounded-full transition-all ${step >= 2 ? 'bg-[#14B8A6]' : 'bg-[#232B2E]'}`} />
          <div className={`h-1.5 rounded-full transition-all ${step >= 3 ? 'bg-[#14B8A6]' : 'bg-[#232B2E]'}`} />
        </div>

        {/* Wizard Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* STEP 1: JURISDICTION */}
          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-white">Where does your business pay taxes?</h3>
                <p className="text-xs text-gray-400">
                  AzuraOS automatically applies the correct statutory tax rules, VAT/sales tax rates, and filing standards.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                {[
                  { code: 'US', label: 'United States', sub: 'IRS · Federal & State Self-Employment' },
                  { code: 'GB', label: 'United Kingdom', sub: 'HMRC · Corporation Tax & VAT' },
                  { code: 'ZA', label: 'South Africa', sub: 'SARS · Turnover/Income & 15% VAT' },
                  { code: 'EU', label: 'European Union', sub: 'EU VAT & Member State Corporate' },
                  { code: 'AU', label: 'Australia', sub: 'ATO · Company Tax & 10% GST' },
                  { code: 'IN', label: 'India', sub: 'GST & Corporate Tax Standards' },
                ].map((item) => {
                  const isSelected = selectedJurisdiction === item.code;
                  return (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => setSelectedJurisdiction(item.code)}
                      className={`p-3.5 rounded-2xl border text-left transition-all flex items-start justify-between cursor-pointer ${
                        isSelected 
                          ? 'border-[#14B8A6] bg-[#14B8A6]/10 text-white shadow-xs' 
                          : 'border-[#232B2E] bg-[#121719] text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <div>
                        <div className="text-sm font-bold flex items-center gap-1.5">
                          <span>{item.label}</span>
                          {isSelected && <CheckCircle2 size={14} className="text-[#14B8A6]" />}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{item.sub}</div>
                      </div>
                      <span className="text-xs font-mono font-bold text-gray-400">
                        {JURISDICTION_PRESETS[item.code]?.currencySymbol}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="p-3.5 bg-[#121719] rounded-2xl border border-[#232B2E] flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-gray-300">
                  <ShieldCheck size={16} className="text-[#14B8A6] shrink-0" />
                  <span>Selected authority: <strong className="text-white">{currentPreset.taxAuthorityLabel}</strong></span>
                </div>
                <span className="font-mono text-teal-400 font-bold">{currentPreset.vatRate}% Base VAT</span>
              </div>
            </div>
          )}

          {/* STEP 2: MONTHLY ESTIMATES */}
          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-white">What are your typical monthly figures?</h3>
                <p className="text-xs text-gray-400">
                  Estimates are totally fine. You can adjust anytime or drop real bank statements later.
                </p>
              </div>

              {/* 1-Click Quick Presets */}
              <div className="space-y-1.5 pt-1">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Quick 1-Click Samples:
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleApplyPreset(selectedJurisdiction === 'ZA' ? 25000 : 6500, selectedJurisdiction === 'ZA' ? 7000 : 1500, 'Solo Freelancer')}
                    className="px-3 py-1.5 bg-[#151B1D] hover:bg-[#1E2629] border border-[#2A3438] rounded-xl text-xs font-medium text-gray-200 transition-colors"
                  >
                    Solo Freelancer
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset(selectedJurisdiction === 'ZA' ? 55000 : 15000, selectedJurisdiction === 'ZA' ? 18000 : 4200, 'Consultant')}
                    className="px-3 py-1.5 bg-[#151B1D] hover:bg-[#1E2629] border border-[#2A3438] rounded-xl text-xs font-medium text-gray-200 transition-colors"
                  >
                    Consultant / Advisory
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset(selectedJurisdiction === 'ZA' ? 120000 : 35000, selectedJurisdiction === 'ZA' ? 38000 : 11000, 'Small Studio')}
                    className="px-3 py-1.5 bg-[#151B1D] hover:bg-[#1E2629] border border-[#2A3438] rounded-xl text-xs font-medium text-gray-200 transition-colors"
                  >
                    Small Studio / Agency
                  </button>
                </div>
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-300 flex items-center justify-between">
                    <span>Monthly Client Inflows</span>
                    <span className="text-[11px] text-gray-400 font-mono">Gross Revenue</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold font-mono">
                      {currencySym}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={500}
                      value={revenueInput}
                      onChange={(e) => setRevenueInput(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full bg-[#121719] border border-[#232B2E] focus:border-[#14B8A6] rounded-xl py-2.5 pl-8 pr-4 text-sm font-bold text-white font-mono focus:outline-hidden transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-300 flex items-center justify-between">
                    <span>Monthly Operating Costs</span>
                    <span className="text-[11px] text-gray-400 font-mono">Software, Tools, Rent</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold font-mono">
                      {currencySym}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={100}
                      value={expenseInput}
                      onChange={(e) => setExpenseInput(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full bg-[#121719] border border-[#232B2E] focus:border-[#14B8A6] rounded-xl py-2.5 pl-8 pr-4 text-sm font-bold text-white font-mono focus:outline-hidden transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Live Preview Bar */}
              <div className="p-4 bg-[#121719] rounded-2xl border border-[#232B2E] space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Operating Margin</span>
                  <span className="font-mono font-bold text-emerald-400">{operatingMargin.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-emerald-400 transition-all duration-300"
                    style={{ width: `${Math.min(100, (safeSpendableCash / (revenueInput || 1)) * 100)}%` }}
                    title="Safe Spend"
                  />
                  <div 
                    className="bg-amber-400 transition-all duration-300"
                    style={{ width: `${Math.min(100, (totalTaxReserve / (revenueInput || 1)) * 100)}%` }}
                    title="Tax Reserve"
                  />
                  <div 
                    className="bg-gray-600 transition-all duration-300"
                    style={{ width: `${Math.min(100, (expenseInput / (revenueInput || 1)) * 100)}%` }}
                    title="Overhead"
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Safe to Spend
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Tax Reserve
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" /> Overhead
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: GOLDEN OUTPUT */}
          {step === 3 && (
            <div className="space-y-4 animate-fade-in">
              <div className="text-center space-y-1">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
                  <Sparkles size={13} />
                  <span>Your 10-Second Tax Breakdown</span>
                </div>
                <h3 className="text-xl font-extrabold text-white">Here is your exact financial plan</h3>
                <p className="text-xs text-gray-400">
                  Separating what is yours to spend from what belongs to {currentPreset.taxAuthorityLabel}.
                </p>
              </div>

              {/* The 3 Golden Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                {/* 1. Safe to Spend Cash */}
                <div className="p-4 bg-emerald-950/20 border border-emerald-500/30 rounded-2xl flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider">
                      Safe to Spend
                    </span>
                    <div className="text-2xl font-black font-mono text-emerald-300 tabular-nums">
                      {currencySym}{safeSpendableCash.toLocaleString()}
                    </div>
                  </div>
                  <p className="text-[11px] text-emerald-200/70 mt-2 leading-tight">
                    Safe for owner draw, dividends, and personal spending without tax risk.
                  </p>
                </div>

                {/* 2. Locked Tax Reserve */}
                <div className="p-4 bg-amber-950/20 border border-amber-500/30 rounded-2xl flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
                      Locked Tax Reserve
                    </span>
                    <div className="text-2xl font-black font-mono text-amber-300 tabular-nums">
                      {currencySym}{totalTaxReserve.toLocaleString()}
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-200/70 mt-2 leading-tight">
                    Auto-partitioned for {currentPreset.taxAuthorityLabel}. Ring-fence in a separate bank account.
                  </p>
                </div>

                {/* 3. Operating Overhead */}
                <div className="p-4 bg-[#121719] border border-[#232B2E] rounded-2xl flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Operating Costs
                    </span>
                    <div className="text-2xl font-black font-mono text-white tabular-nums">
                      {currencySym}{expenseInput.toLocaleString()}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2 leading-tight">
                    Monthly recurring software, subscriptions, and contractor overhead.
                  </p>
                </div>
              </div>

              {/* Download CSV summary button */}
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={handleDownloadSummary}
                  className="inline-flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 font-mono hover:underline cursor-pointer"
                >
                  <Download size={13} />
                  <span>Download 1-Page Tax Plan Summary (.CSV)</span>
                </button>
              </div>

              {/* Workspace Choice */}
              <div className="pt-2 border-t border-[#232B2E] space-y-2.5">
                <span className="text-xs font-bold text-gray-300 block text-center">
                  How would you like to explore your workspace?
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleFinish('quick')}
                    className="p-3.5 bg-gradient-to-br from-[#14B8A6] to-emerald-500 hover:opacity-95 text-black rounded-2xl font-bold text-xs flex flex-col items-center justify-center gap-1 shadow-lg transition-all cursor-pointer group"
                  >
                    <span className="flex items-center gap-1.5 text-sm font-black">
                      <Zap size={16} />
                      <span>Quick Clarity Mode</span>
                    </span>
                    <span className="text-[11px] text-black/80 font-normal">
                      Simple 3-metric dashboard + 1-click receipts
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleFinish('power')}
                    className="p-3.5 bg-[#151B1D] hover:bg-[#1E2629] border border-teal-500/40 hover:border-teal-400 text-white rounded-2xl font-bold text-xs flex flex-col items-center justify-center gap-1 transition-all cursor-pointer group"
                  >
                    <span className="flex items-center gap-1.5 text-sm font-bold text-teal-300">
                      <Sliders size={15} />
                      <span>Full Power OS Mode</span>
                    </span>
                    <span className="text-[11px] text-gray-400 font-normal">
                      All 12 modules: Invoices, Calendar, BYOK AI
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Wizard Footer Controls */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#232B2E] bg-[#121719]">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => (s - 1) as 1 | 2)}
              className="px-4 py-2 rounded-xl border border-[#232B2E] text-xs font-bold text-gray-300 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Skip
            </button>
          )}

          {step < 3 && (
            <button
              type="button"
              onClick={() => setStep((s) => (s + 1) as 2 | 3)}
              className="px-5 py-2.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black text-xs font-black flex items-center gap-1.5 transition-all shadow-md cursor-pointer"
            >
              <span>{step === 1 ? 'Continue to Figures' : 'Calculate My Taxes'}</span>
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
